import React from 'react';
import { Box, Container, Paper, Stack, Typography, alpha, useTheme } from '@mui/material';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AddHomeOutlinedIcon from '@mui/icons-material/AddHomeOutlined';
import BedOutlinedIcon from '@mui/icons-material/BedOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import { useTranslation } from 'react-i18next';

const LOGO_LIGHT = '/brand/odazen-logo-light.png';

/** Sol paneldeki dekoratif ürün önizlemesi (gerçek ilan değil) */
const Preview = () => {
  const { t } = useTranslation();
  const glass = {
    bgcolor: alpha('#0F1E3D', 0.7),
    border: `1px solid ${alpha('#fff', 0.1)}`,
    borderRadius: '16px',
    backdropFilter: 'blur(14px)',
    boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)',
  };
  return (
    <Box
      aria-hidden
      sx={{ position: 'relative', height: 220, mt: 4.5, maxWidth: 440, '@media (max-height: 800px)': { display: 'none' } }}
    >
      <Box sx={{ ...glass, position: 'absolute', left: 0, top: 0, width: 280, overflow: 'hidden' }}>
        <Box
          sx={{
            height: 104,
            display: 'grid',
            placeItems: 'center',
            background: 'linear-gradient(135deg, #1E3A8A 0%, #2563EB 55%, #60A5FA 100%)',
            color: alpha('#fff', 0.85),
          }}
        >
          <BedOutlinedIcon sx={{ fontSize: 40 }} />
        </Box>
        <Box sx={{ p: 1.75 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 650, color: '#F1F5F9' }}>{t('auth.shell.previewListing')}</Typography>
          <Stack direction="row" spacing={0.75} sx={{ mt: 1 }}>
            {[t('auth.shell.previewChip1'), t('auth.shell.previewChip2')].map((c) => (
              <Box key={c} sx={{ px: 1, py: 0.25, borderRadius: '6px', fontSize: 11.5, fontWeight: 600, color: '#BFDBFE', bgcolor: alpha('#3B82F6', 0.2) }}>
                {c}
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
      <Box sx={{ ...glass, position: 'absolute', right: 0, bottom: 0, width: 240, p: 1.75, display: 'flex', gap: 1.25, alignItems: 'center' }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', display: 'grid', placeItems: 'center', bgcolor: alpha('#10B981', 0.18), color: '#6EE7B7', flexShrink: 0 }}>
          <PeopleAltOutlinedIcon sx={{ fontSize: 19 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 650, color: '#F1F5F9' }}>{t('auth.shell.previewMatchTitle')}</Typography>
          <Typography sx={{ fontSize: 11.5, color: alpha('#fff', 0.62) }}>{t('auth.shell.previewMatchText')}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Odazen giriş / kayıt sayfaları için iki kolonlu kart.
 * Site Layout'u (navbar + footer) içinde render edilir.
 */
const AuthShell = ({ children, maxFormWidth = 420 }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const features = [
    { icon: VerifiedUserOutlinedIcon, title: t('auth.shell.f1Title'), text: t('auth.shell.f1Text') },
    { icon: ChatBubbleOutlineIcon, title: t('auth.shell.f2Title'), text: t('auth.shell.f2Text') },
    { icon: AddHomeOutlinedIcon, title: t('auth.shell.f3Title'), text: t('auth.shell.f3Text') },
  ];

  return (
    <Box
      sx={{
        minHeight: { xs: 'calc(100vh - 70px)', md: 'calc(100vh - 80px)' },
        display: 'flex',
        alignItems: 'center',
        py: { xs: 3, md: 5 },
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <Paper
          elevation={0}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '0.95fr 1fr' },
            borderRadius: '24px',
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
            boxShadow: isDark ? '0 30px 60px -30px rgba(0,0,0,0.7)' : '0 30px 60px -30px rgba(15,23,42,0.25)',
          }}
        >
          {/* Marka paneli */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              justifyContent: 'space-between',
              p: { md: 5, lg: 6 },
              color: '#fff',
              position: 'relative',
              overflow: 'hidden',
              background: 'linear-gradient(155deg, #081226 0%, #0B1F4A 50%, #1640A8 120%)',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                backgroundImage: `linear-gradient(${alpha('#fff', 0.045)} 1px, transparent 1px), linear-gradient(90deg, ${alpha('#fff', 0.045)} 1px, transparent 1px)`,
                backgroundSize: '40px 40px',
                maskImage: 'radial-gradient(ellipse at 30% 30%, #000 15%, transparent 72%)',
                WebkitMaskImage: 'radial-gradient(ellipse at 30% 30%, #000 15%, transparent 72%)',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                width: 480,
                height: 480,
                right: -200,
                bottom: -220,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${alpha('#3B82F6', 0.42)} 0%, transparent 65%)`,
              },
            }}
          >
            <Box component="img" src={LOGO_LIGHT} alt="Odazen" sx={{ position: 'relative', zIndex: 1, height: 96, width: 96, objectFit: 'contain', m: -2, ml: -2.5 }} />
            <Box sx={{ position: 'relative', zIndex: 1, mt: 3 }}>
              <Typography sx={{ fontSize: { md: '1.9rem', lg: '2.25rem' }, fontWeight: 750, letterSpacing: '-0.03em', lineHeight: 1.15, maxWidth: 440 }}>
                {t('auth.shell.title')}
              </Typography>
              <Typography sx={{ mt: 1.75, color: alpha('#fff', 0.7), lineHeight: 1.6, maxWidth: 420 }}>{t('auth.shell.subtitle')}</Typography>
              <Stack spacing={2} sx={{ mt: 3.5 }}>
                {features.map((f) => {
                  const Icon = f.icon;
                  return (
                    <Stack key={f.title} direction="row" spacing={1.5} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: '10px',
                          display: 'grid',
                          placeItems: 'center',
                          flexShrink: 0,
                          bgcolor: alpha('#fff', 0.08),
                          border: `1px solid ${alpha('#fff', 0.12)}`,
                          color: '#93C5FD',
                        }}
                      >
                        <Icon sx={{ fontSize: 18 }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 650, fontSize: '0.92rem' }}>{f.title}</Typography>
                        <Typography sx={{ color: alpha('#fff', 0.62), fontSize: '0.85rem', mt: 0.25 }}>{f.text}</Typography>
                      </Box>
                    </Stack>
                  );
                })}
              </Stack>
              <Preview />
            </Box>
          </Box>

          {/* Form */}
          <Box sx={{ display: 'grid', placeItems: 'center', px: { xs: 3, sm: 6 }, py: { xs: 4, sm: 6 } }}>
            <Box sx={{ width: '100%', maxWidth: maxFormWidth }}>{children}</Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default AuthShell;
